import type { MissionId, MissionLockingInfo, Segment } from '../../../types/gcs/mission';

/**
 * A locked segment is only drawable when it spans at least two distinct, in-range route nodes.
 * Mirrors the guard used when rendering locked-segment polylines.
 */
export const isSegmentValid = (routeLength: number, segment: Segment): boolean => {
  if (segment.endIndex <= segment.startIndex) return false;
  return (
    segment.startIndex >= 0 &&
    segment.startIndex < routeLength &&
    segment.endIndex >= 0 &&
    segment.endIndex < routeLength
  );
};

/**
 * Route lines (nominal route, route-to-safe-point) must stack below the locked-segment lines,
 * which they do via a Mapbox `beforeId` pointing at a locked-segment layer. But mapbox-gl drops
 * any layer whose `beforeId` does not exist, so anchoring to a locked-segment layer that was
 * never rendered (no locking info, or all segments invalid) makes the route line vanish.
 *
 * This is the cause of ROC-90: on the way back inside the control zone there are no locked
 * segments, so after a page refresh the route <Layer> is re-added with a dangling beforeId and
 * mapbox silently skips it — the route disappears while the drone marker remains.
 *
 * Returns the id of the first locked-segment layer that will actually be rendered for this
 * mission, or `undefined` when none will be — in which case the caller should omit `beforeId`
 * (the layer is added on top, and moves below once locking info arrives and this recomputes).
 *
 * The index must match the array index used as the layer suffix in the locked-segments
 * component, which skips invalid segments without reindexing.
 */
export const getLockedSegmentAnchorLayerId = (
  gcsId: string,
  missionId: MissionId,
  routeLength: number,
  lockingInfo: MissionLockingInfo | undefined
): string | undefined => {
  const segments = lockingInfo?.lockedSegmentsIndices;
  if (!segments || segments.length === 0) return undefined;

  const firstValidIndex = segments.findIndex((segment) => isSegmentValid(routeLength, segment));
  if (firstValidIndex === -1) return undefined;

  return `locked-segment-line-${gcsId}-${missionId}-${firstValidIndex}`;
};
